import React from "react";
import PropTypes from "prop-types";
import MarkPaidForm from "./MarkPaidForm";

// Why the payment goes to the Payment Accountant instead of straight to HSBC.
const REASON = {
  trade_loan: "Trade loan — always handled by the Payment Accountant.",
  non_hsbc: "Not paid through HSBC — the Payment Accountant pays it outside the Portal and records it here.",
  setting: "HSBC payments go through the Payment Accountant (Payment Request settings).",
};

const STATUS = {
  expected:   { label: "After final approval", tone: "tw-bg-geist-100 tw-text-geist-fg-2" },
  processing: { label: "Processing", tone: "tw-bg-[#fff8ee] tw-text-[#a05e03]" },
  paid:       { label: "Paid", tone: "tw-bg-[#edfaef] tw-text-geist-success" },
};

/**
 * Payment Accountant (kế toán chi tiền) — the step after BOD: Processing → Paid.
 * HSBC payments become Paid when HSBC confirms; Non-HSBC ones when the team records it.
 * To pay a Non-HSBC request through HSBC instead, the team changes the bank (below):
 * Head of Finance and BOD then sign again.
 */
const DisbursementPanel = ({ disbursement, hsbc, busy, canMarkPaid, onMarkPaid }) => {
  const status = STATUS[disbursement.status] || STATUS.expected;
  const team = (disbursement.team || []).map((m) => m.name || m.email).join(", ");

  return (
    <div className="tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-flex-wrap">
        <span className="tw-text-[12px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3">Payment Accountant</span>
        <span className="tw-flex-1" />
        <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-rounded tw-text-[12px] tw-font-bold ${status.tone}`}>
          {status.label}
        </span>
      </div>
      <p className="tw-text-[13px] tw-text-geist-fg-2 tw-m-0">{REASON[disbursement.reason] || "Handled by the Payment Accountant."}</p>
      <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-1">{team || "No Payment Accountant configured — ask an administrator."}</p>
      {disbursement.status === "paid" && disbursement.paid_by && (
        <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-1">
          Recorded by {disbursement.paid_by}
          {disbursement.reference ? ` · ${disbursement.reference}` : ""}
          {disbursement.note && <span className="tw-block tw-italic">“{disbursement.note}”</span>}
        </p>
      )}
      {disbursement.status === "processing" && hsbc && (
        <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-1">Send the instruction to HSBC below; it shows Paid once HSBC confirms.</p>
      )}

      {canMarkPaid && <MarkPaidForm className="tw-mt-2.5" busy={busy} onMarkPaid={onMarkPaid} />}
    </div>
  );
};

DisbursementPanel.propTypes = {
  disbursement: PropTypes.shape({
    status: PropTypes.string,
    reason: PropTypes.string,
    team: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    })),
    paid_by: PropTypes.string,
    reference: PropTypes.string,
    note: PropTypes.string,
  }).isRequired,
  hsbc: PropTypes.bool,
  busy: PropTypes.bool,
  canMarkPaid: PropTypes.bool,
  onMarkPaid: PropTypes.func,
};

export default DisbursementPanel;
